import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button } from '../Button';
import { LoadingButton } from '../LoadingButton';
import { FormTextField } from '../FormTextField';
import { Dialog } from '../Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { formatNoteDate } from 'utils/dates';
import { sanitizeAndParseHtml, sanitizeToPlainText } from 'utils/sanitizeHtml';

export interface FormattedNote {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  creator: string;
}

interface NotesForm {
  title: string;
  content: string;
}

interface NotesProps {
  notes: FormattedNote[];
  onCreateNote: (title: string, content: string) => Promise<void>;
  isLoading?: boolean;
}

export function Notes({ notes, onCreateNote, isLoading = false }: NotesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const form = useForm<NotesForm>({
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const handleCreate = async (data: NotesForm) => {
    if (!data.title.trim() || !data.content.trim() || isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      // Sanitize both title and content to remove all HTML and keep only plain text
      const sanitizedTitle = sanitizeToPlainText(data.title);
      const sanitizedContent = sanitizeToPlainText(data.content);

      if (!sanitizedTitle || !sanitizedContent) {
        return;
      }

      await onCreateNote(sanitizedTitle, sanitizedContent);
      form.reset();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to create note:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isCreating) return; // Prevent closing during creation
    setIsModalOpen(false);
    form.reset();
  };

  return (
    <div className='flex h-full w-full flex-col' style={{ overflow: 'visible' }}>
      <div className={'flex justify-end mr-4 shrink-0'}>
        <Button variant={'contained'} onClick={handleOpenModal} disabled={isLoading}>
          New Note
        </Button>
      </div>
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth={'md'} fullWidth>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleCreate)}>
            <DialogTitle>Create Note</DialogTitle>
            <DialogContent>
              <div className={'mt-4'}>
                <div className={'mb-4'}>
                  <FormTextField
                    name={'title'}
                    textFieldProps={{ label: 'Title' }}
                    disabled={isCreating || isLoading}
                  />
                </div>
                <div className={'mb-4'}>
                  <FormTextField
                    name={'content'}
                    textFieldProps={{ label: 'Content', multiline: true, rows: 4 }}
                    disabled={isCreating || isLoading}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} disabled={isCreating || isLoading}>
                Cancel
              </Button>
              <LoadingButton
                isLoading={isCreating || isLoading}
                buttonProps={{
                  variant: 'contained',
                  type: 'submit',
                  disabled: isCreating || isLoading || !form.watch('title')?.trim() || !form.watch('content')?.trim(),
                }}
              >
                {isCreating || isLoading ? 'Creating...' : 'Create'}
              </LoadingButton>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <div className={'border border-gray-300 rounded-lg px-4 mt-2 flex flex-col flex-1 min-h-0'}>
        {notes.length === 0 ? (
          <p className={'text-gray-500 text-center py-4'}>No notes yet</p>
        ) : (
          <div className={'custom-scrollbar h-full space-y-4 overflow-y-auto py-2'}>
            {notes.map(note => (
              <div key={note.id} className={'bg-white rounded-lg px-4 py-1 border border-gray-200'}>
                <div className={'flex justify-between items-center mb-2'}>
                  <h4 className={'font-semibold text-base m-0'}>{note.title}</h4>
                  <span className={'text-xs text-gray-500'}>
                    {formatNoteDate(note.createdAt)} by {note.creator.toLowerCase()}
                  </span>
                </div>
                <div className={'text-sm text-gray-700 mb-2'} style={{ wordBreak: 'break-word' }}>
                  {sanitizeAndParseHtml(note.content)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
